import type { DataSetVersionsResponse, PaginatedTntData } from '../../timeInTransit/types';
import GatewayAxiosClient from '../GatewayAxiosClient';
import type {
  TntDataSetType,
  TntDataSet,
  TntSource,
  TntRecordsResponse,
  DownloadTntResponse,
  TntDataSetTypesSearchBody,
  UploadProcessTntRecordsRequest,
  UploadTntResponse,
  TntJob,
  CreateDataSet,
  UploadDataSet,
  UpdateDataSetTypeFromUploadRequestV2,
  UploadProcessTntRecordsRequestV2,
  TntJobV2,
  DownloadTntRecordsRequestV2,
  DataSetResponseV2,
} from './types';

const TNT_PROXY_PATH = '/tnt-management-proxy/api/v1';

export class TimeInTransitService {
  public async fetchTntDataSetTypes(
    body: TntDataSetTypesSearchBody = {},
    delegatedPartnerId?: string,
  ): Promise<TntDataSetType[]> {
    let headers;
    if (delegatedPartnerId) {
      headers = {
        'x-delegated-partner-id': delegatedPartnerId,
      };
    }

    return (
      await GatewayAxiosClient.post(
        `${TNT_PROXY_PATH}/tnt/data-set-types/search`,
        {
          ...body,
        },
        {
          headers,
        },
      )
    ).data;
  }
  public async fetchTntDataSets(dataSetTypeId: string, latestUsable?: boolean): Promise<{ dataSets: TntDataSet[] }> {
    return (
      await GatewayAxiosClient.get(`${TNT_PROXY_PATH}/tnt/data-set-types/${dataSetTypeId}/data-sets`, {
        params: {
          latestUsable,
        },
      })
    ).data;
  }
  public async fetchTntSources(): Promise<TntSource[]> {
    return (await GatewayAxiosClient.get(`${TNT_PROXY_PATH}/tnt-sources`)).data;
  }
  public async fetchTntRecords(dataSetId: string, params: any): Promise<TntRecordsResponse> {
    return (
      await GatewayAxiosClient.get(`${TNT_PROXY_PATH}/tnt/data-sets/${dataSetId}/records`, {
        params,
      })
    ).data;
  }
  public async fetchTntRecordsByVersion(tntDataSetVersionId: string, params: any): Promise<PaginatedTntData[]> {
    return (
      await GatewayAxiosClient.post(
        `/tnt-management-proxy/api/v2/tnt/data-set-versions/${tntDataSetVersionId}/records/search`,
        {
          ...params,
        },
      )
    ).data;
  }
  public async fetchTntJobs(dataSetTypeId?: string): Promise<TntJob[]> {
    return (
      await GatewayAxiosClient.get(`${TNT_PROXY_PATH}/tnt/files/jobs`, {
        params: {
          dataSetTypeId,
        },
      })
    ).data;
  }
  public async fetchTntJobById(jobId: string, params: any): Promise<TntJob> {
    return (
      await GatewayAxiosClient.get(`${TNT_PROXY_PATH}/tnt/files/jobs/${jobId}`, {
        params,
      })
    ).data;
  }
  public async fetchTntJobsV2(tntDataSetId?: string): Promise<TntJobV2[]> {
    return (
      await GatewayAxiosClient.get(`/tnt-management-proxy/api/v2/tnt/files/jobs`, {
        params: {
          tntDataSetId,
          ignorePartnerId: true,
        },
      })
    ).data;
  }
  public async fetchTntJobByIdV2(jobId: string): Promise<TntJobV2> {
    return (await GatewayAxiosClient.get(`/tnt-management-proxy/api/v2/tnt/files/jobs/${jobId}`)).data;
  }
  public async fetchDataSetsV2(): Promise<DataSetResponseV2[]> {
    return (await GatewayAxiosClient.get(`/tnt-management-proxy/api/v2/tnt/data-sets`)).data;
  }
  public async fetchDataSetVersions(tntDataSetId: string): Promise<DataSetVersionsResponse> {
    return (
      await GatewayAxiosClient.get(`/tnt-management-proxy/api/v2/tnt/data-sets/${tntDataSetId}/data-set-versions`)
    ).data;
  }
  public async fetchDataSetVersionsBulk(
    tntDataSetIds: string[],
  ): Promise<{ [key: string]: DataSetVersionsResponse[] }> {
    return (
      await GatewayAxiosClient.post(`/ui-orchestration-proxy/api/v1/tnt/batch-data-set-versions`, {
        tntDataSetIds,
      })
    ).data;
  }
  public async fetchTntJobsBulk(dataSetTypeIds: string[]): Promise<{ [key: string]: TntJob[] }> {
    return (
      await GatewayAxiosClient.post(`/ui-orchestration-proxy/api/v1/tnt/batch-jobs`, {
        dataSetTypeIds,
      })
    ).data;
  }
  public async fetchTntDataSetsBulk(dataSetTypeIds: string[]): Promise<{ [key: string]: TntDataSet[] }> {
    return (
      await GatewayAxiosClient.post(`/ui-orchestration-proxy/api/v1/tnt/batch-data-sets`, {
        dataSetTypeIds,
      })
    ).data;
  }
  public async downloadTntRecordsBySearch(body: DownloadTntRecordsRequestV2 = {}): Promise<DownloadTntResponse> {
    return (
      await GatewayAxiosClient.post(`${TNT_PROXY_PATH}/tnt/files/download`, {
        ...body,
      })
    ).data;
  }
  public async downloadTntRecordsBySearchV2(body: DownloadTntRecordsRequestV2 = {}): Promise<DownloadTntResponse> {
    return (
      await GatewayAxiosClient.post(`/tnt-management-proxy/api/v2/tnt/files/download`, {
        ...body,
      })
    ).data;
  }
  public async createAsyncDataSetTypeFromUpload(body: UploadProcessTntRecordsRequest): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.post(`${TNT_PROXY_PATH}/tnt/files/data-set-types`, {
        ...body,
      })
    ).data;
  }
  public async createAsyncDataSetTypeFromUploadV2(body: UploadProcessTntRecordsRequestV2): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.post(`/tnt-management-proxy/api/v2/tnt/files/data-sets`, {
        ...body,
      })
    ).data;
  }
  public async updateDataSetTypeFromUpload(
    dataSetTypeId: string,
    body: UploadProcessTntRecordsRequest,
  ): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.post(`${TNT_PROXY_PATH}/tnt/files/data-set-types/${dataSetTypeId}`, {
        ...body,
      })
    ).data;
  }
  public async updateDataSetTypeFromUploadV2(
    tntDataSetId: string,
    body: UpdateDataSetTypeFromUploadRequestV2,
  ): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.post(`/tnt-management-proxy/api/v2/tnt/files/data-sets/${tntDataSetId}`, {
        ...body,
      })
    ).data;
  }
  public async updateDataSetType(body: TntDataSetType): Promise<TntRecordsResponse> {
    return (
      await GatewayAxiosClient.put(`${TNT_PROXY_PATH}/tnt/data-set-types`, {
        ...body,
      })
    ).data;
  }
  public async deleteDataSetType(dataSetTypeId: string): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.delete(`${TNT_PROXY_PATH}/tnt/data-set-types/${dataSetTypeId}`, {
        params: {
          dataSetTypeId,
        },
      })
    ).data;
  }
  public async createDataSet(body: CreateDataSet): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.post(`${TNT_PROXY_PATH}/tnt/data-sets`, {
        ...body,
        writeCompleted: true,
      })
    ).data;
  }
  public async uploadDataSet(dataSetTypeId: string, body: UploadDataSet): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.post(`${TNT_PROXY_PATH}/tnt/files/data-set-types/${dataSetTypeId}`, {
        ...body,
      })
    ).data;
  }
  public async deleteDataSet(dataSetId: string): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.patch(`${TNT_PROXY_PATH}/tnt/data-sets/${dataSetId}`, {
        deleted: true,
      })
    ).data;
  }
  public async writeDataSet(dataSetId: string): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.patch(`${TNT_PROXY_PATH}/tnt/data-sets/${dataSetId}`, {
        writeCompleted: true,
      })
    ).data;
  }
  public async editDataSet(body: any): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.patch(`${TNT_PROXY_PATH}/tnt/data-sets/${body.dataSetId}`, {
        description: body.description,
      })
    ).data;
  }
  public async activateTnt(dataSetTypeId: string): Promise<UploadTntResponse> {
    return (
      await GatewayAxiosClient.post(`${TNT_PROXY_PATH}/active-simulation-tnts`, {
        dataSetTypeId: dataSetTypeId,
      })
    ).data;
  }
  public async deactivateTnt(dataSetTypeId: string): Promise<UploadTntResponse> {
    return (await GatewayAxiosClient.delete(`${TNT_PROXY_PATH}/active-simulation-tnts/${dataSetTypeId}`, {})).data;
  }
  public async getTntStatus(dataSetTypeId: string): Promise<UploadTntResponse> {
    return (await GatewayAxiosClient.get(`${TNT_PROXY_PATH}/active-simulation-tnts/${dataSetTypeId}`, {})).data;
  }
}
